"use client";
import { useState } from "react";
import { Play, Eye, User, ExternalLink } from "lucide-react";

export interface YouTubeVideo {
  videoId: string;
  title: string;
  channelTitle: string;
  thumbnailUrl: string;
  viewCount?: string;
  duration?: string;
}

function formatViewCount(count?: string): string {
  if (!count) return "";
  const n = parseInt(count, 10);
  if (n >= 10000) return `${Math.floor(n / 10000)}万回視聴`;
  if (n >= 1000) return `${Math.floor(n / 1000)}千回視聴`;
  return `${n}回視聴`;
}

function formatDuration(iso?: string): string {
  if (!iso) return "";
  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return "";
  const h = parseInt(match[1] || "0");
  const m = parseInt(match[2] || "0");
  const s = parseInt(match[3] || "0");
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function YouTubeVideoList({ videos }: { videos: YouTubeVideo[] }) {
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {videos.map((video) => (
        <div
          key={video.videoId}
          className="rounded-xl border border-border bg-card overflow-hidden hover:border-border/80 transition-colors"
        >
          {activeVideoId === video.videoId ? (
            <div className="aspect-video">
              <iframe
                src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <button
              onClick={() => setActiveVideoId(video.videoId)}
              className="relative w-full aspect-video group"
            >
              <img
                src={video.thumbnailUrl}
                alt={video.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                </div>
              </div>
              {video.duration && (
                <span className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded font-mono">
                  {formatDuration(video.duration)}
                </span>
              )}
            </button>
          )}
          <div className="p-3 space-y-1.5">
            <h3 className="text-sm font-medium text-foreground line-clamp-2 leading-snug">
              {video.title}
            </h3>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {video.channelTitle}
              </span>
              {video.viewCount && (
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {formatViewCount(video.viewCount)}
                </span>
              )}
            </div>
            <a
              href={`https://www.youtube.com/watch?v=${video.videoId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              YouTubeで見る
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}
